import { useState } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { PhotoPicker } from "@/components/wh/PhotoPicker";

const TEXT = [["category", "Categoria", 80], ["subcategory", "Sottocategoria", 80], ["color", "Colore", 60], ["brand", "Marca", 80],
  ["model", "Modello", 80], ["condition", "Condizioni dell'oggetto", 80], ["location", "Posizione in magazzino", 120]];
const LONG = [["defects", "Eventuali difetti", 1000], ["notes", "Note interne", 2000]];
const MONEY = ["purchase_price", "sale_price", "extra_costs"];
const num = (v) => (v === "" || v == null ? null : Number(v));

function toForm(it, lotId) {
  const f = { lot_id: it?.lot_id || lotId || "", photos: it?.photos || [], quantity: it?.quantity ?? 1, description: it?.description || "" };
  [...TEXT, ...LONG].forEach(([k]) => { f[k] = it?.[k] || ""; });
  MONEY.forEach((k) => { f[k] = it?.[k] ?? ""; });
  if (!it) f.purchase_price = 0;
  return f;
}

function Field({ label, children, wide }) {
  return <div className={`space-y-1.5 ${wide ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;
}

export function ItemForm({ item, lots, lotId, onSaved, onCancel, wh = "mercatino" }) {
  const { user } = useAuth();
  const [f, setF] = useState(() => toForm(item, lotId));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const cost = can(user, "wh_cost");
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, wh: item?.wh || wh, lot_id: f.lot_id || null, quantity: Number(f.quantity) || 1 };
    MONEY.forEach((k) => { body[k] = num(f[k]); });
    try {
      const { data } = item ? await api.put(`/wh/items/${item.id}`, body) : await api.post("/wh/items", body);
      toast.success(item ? "Scheda aggiornata" : `Articolo ${data.code} salvato`);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-5" data-testid="wh-item-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Codice"><div className="h-10 rounded-md border border-dashed border-[var(--border)] px-3 flex items-center text-sm font-mono text-[var(--muted)]" data-testid="wh-form-code">{item?.code || "Generato automaticamente al salvataggio"}</div></Field>
        <Field label={item ? "Quantità totale (pezzi)" : "Pezzi disponibili"}><Input type="number" min={1} value={f.quantity} onChange={set("quantity")} data-testid="wh-form-quantity" /></Field>
        <Field label="Descrizione" wide><Input maxLength={2000} value={f.description} onChange={set("description")} placeholder="es. Credenza in noce anni '60" data-testid="wh-form-description" /></Field>
        {cost && <Field label="Prezzo di acquisto (€ cad.)"><Input type="number" step="0.01" min="0" value={f.purchase_price} onChange={set("purchase_price")} data-testid="wh-form-purchase_price" /></Field>}
        <Field label="Prezzo di vendita (€ cad.)"><Input type="number" step="0.01" min="0" value={f.sale_price} onChange={set("sale_price")} data-testid="wh-form-sale_price" /></Field>
      </div>
      <details className="group rounded-xl border border-[var(--border)]" data-testid="wh-form-more">
        <summary className="cursor-pointer list-none flex items-center justify-between px-4 py-3 text-sm font-semibold" data-testid="wh-form-more-toggle">
          Altri dettagli (facoltativi): foto, lotto, categoria... <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="p-4 pt-0 space-y-4">
          <Field label="Fotografie"><PhotoPicker photos={f.photos} onChange={(photos) => setF({ ...f, photos })} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            {!item && (
              <Field label="Lotto">
                <select value={f.lot_id} onChange={set("lot_id")} disabled={!!lotId && !lots.length} className="h-10 w-full rounded-md border border-[var(--border)] px-3 text-sm bg-white" data-testid="wh-form-lot">
                  <option value="">Senza lotto</option>{lots.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </Field>
            )}
            {TEXT.map(([k, l, m]) => <Field key={k} label={l}><Input maxLength={m} value={f[k]} onChange={set(k)} data-testid={`wh-form-${k}`} /></Field>)}
            {cost && <Field label="Costi aggiuntivi (€ totali)"><Input type="number" step="0.01" min="0" value={f.extra_costs} onChange={set("extra_costs")} data-testid="wh-form-extra_costs" /></Field>}
            {LONG.map(([k, l, m]) => <Field key={k} label={l} wide><Textarea rows={2} maxLength={m} value={f[k]} onChange={set(k)} data-testid={`wh-form-${k}`} /></Field>)}
          </div>
        </div>
      </details>
      <div className="flex gap-2">
        <button disabled={busy} className="btn-primary h-11 px-6" data-testid="wh-form-save">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Salva articolo</button>
        <button type="button" onClick={onCancel} className="btn-secondary h-11 px-5" data-testid="wh-form-cancel">Annulla</button>
      </div>
    </form>
  );
}
